import { Alert, Anchor, Breadcrumbs, Text } from '@mantine/core';
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import BannerBlockForm from '../../../components/BannerBlocks/BannerBlockForm';
import ErrorMessage from '../../../components/ErrorMessage';
import Metadata from '../../../components/Metadata';
import Spinner from '../../../components/Spinner';
import useRestorePlacementSession from '../../../components/useRestorePlacementSession';
import {
  DEFAULT_CHROME_CSS_STYLES,
  EMPTY_CHROME_CSS_STYLES,
  MODAL_DISPLAY_TYPES,
} from '../../../constants/bannerChrome';
import { Page } from '../../../constants/pageTitles';
import useFetch from '../../../helpers/useFetch';

const BannerBlocksDetailsPage = ({ type = 'bannerBlocks' }) => {
  const page = `${Page[type]}`;
  const location = useLocation();
  const navigate = useNavigate();
  const fetch = useFetch();

  const [segments, setSegments] = useState();
  const [loadError, setLoadError] = useState();
  const [bannerBlock, setBannerBlock] = useState();
  const { accountId, domainId, pageId, id } = useParams();
  const typeName = type === 'postPurchases' ? 'Post Purchase' : 'Banner Block';

  // Coming back from positioning the banner on the customer's own site: the
  // unsaved form the merchant left with is on the session, and the position
  // they picked is laid over it. Read here rather than in the form so the form
  // is built from the restored block instead of being rewritten after mount —
  // this page's spinner covers the read, so there is never an editable,
  // saveable form standing on the pre-trip block. The placement routes are
  // hyphenated (`banner-blocks`), unlike this page's own `bannerBlocks` reads.
  const placement = useRestorePlacementSession({
    blockPath: `/api/admin/accounts/${accountId}/domains/${domainId}/pages/${pageId}/banner-blocks/${id}`,
  });

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/segments?scope=internal`)
      .then(({ segments }) => {
        setSegments(segments);
      })
      .catch(setLoadError);
  }, [fetch, accountId, domainId]);

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/pages/${pageId}/bannerBlocks/${id}`)
      .then(({ bannerBlock }) => {
        if (!bannerBlock) {
          navigate(`/accounts/${accountId}/domains/${domainId}/pages?page=${pageId}&type=${type}`);
        }

        if (bannerBlock.cssStyles === null) {
          // Seed the chrome the block's display mode draws: the defaults for
          // the modes the SDK wraps in a card, the blank shape for the rest.
          bannerBlock.cssStyles = MODAL_DISPLAY_TYPES.includes(bannerBlock.displayType)
            ? { ...DEFAULT_CHROME_CSS_STYLES }
            : { ...EMPTY_CHROME_CSS_STYLES };
        }
        setBannerBlock(bannerBlock);
      })
      .catch(setLoadError);
  }, [fetch, navigate, type, accountId, domainId, pageId, id]);

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (bannerBlock === undefined || segments === undefined || placement.pending) {
    return <Spinner />;
  }

  return (
    <>
      <Metadata title={`${page} | ${bannerBlock.name} | Edit`} />
      <Breadcrumbs mb="md">
        <Anchor href={`#/accounts/${accountId}/domains/${domainId}/analytics`}>Home</Anchor>
        <Anchor
          href={`#/accounts/${accountId}/domains/${domainId}/pages?type=${type}&page=${bannerBlock?.pageId || ''}`}
        >
          {bannerBlock.page.name} {typeName}
        </Anchor>
        <Anchor href={`#${location.pathname.replace('details', 'analytics')}`}>{bannerBlock.name}</Anchor>
        <Text>Edit</Text>
      </Breadcrumbs>
      {placement.notice && (
        <Alert color="blue" icon={<IconInfoCircle size={16} />} mb="md">
          <Text size="xs">{placement.notice}</Text>
        </Alert>
      )}
      {placement.error && (
        // Closable, unlike the notice: saving is the answer to "press Save to
        // keep it", but there is nothing to save in response to a session that
        // could not be read, so otherwise this would sit here for the visit.
        // `dismiss` rather than `onSaved` — closing the message must not cost
        // the merchant the reload the message just offered them.
        <Alert
          color="orange"
          icon={<IconAlertTriangle size={16} />}
          mb="md"
          withCloseButton
          closeButtonLabel="Dismiss"
          onClose={placement.dismiss}
        >
          <Text size="xs">{placement.error}</Text>
        </Alert>
      )}
      <BannerBlockForm
        type={type}
        // The restored block when the merchant came back from a positioning
        // trip, the saved one otherwise. Whichever it is, the form is mounted
        // on it once and nothing replaces it afterwards. The tab the trip
        // started from rides back on the route the shop page returns to, so a
        // restored form opens on the display configuration by itself.
        bannerBlock={placement.restored || bannerBlock}
        segments={segments}
        // "press Save to keep it" stops applying once they have — and a save is
        // also what makes a stored snapshot stale, so this is where the session
        // token is retired if the read never succeeded.
        onSaved={placement.onSaved}
        cancel={() =>
          navigate(`/accounts/${accountId}/domains/${domainId}/pages?type=${type}&page=${bannerBlock?.pageId || ''}`)
        }
      />
    </>
  );
};

export default BannerBlocksDetailsPage;
